import * as Sentry from '@sentry/react'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AppErrorBoundary } from '@/components/ErrorBoundary'
import { ThemeProvider } from 'next-themes'
import Index from './pages/Index'
import Episode from './pages/Episode'
import Quiz from './pages/Quiz'
import AccessSymphony from './pages/AccessSymphony'
import UserProfile from './pages/UserProfile'
import Auth from './pages/Auth'
import AuthCallback from './pages/AuthCallback'
import SignUp from './pages/SignUp'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import CheckEmail from './pages/CheckEmail'
import VerifyEmail from './pages/VerifyEmail'
import Admin from './pages/Admin'
import SuperAdmin from './pages/SuperAdmin'
import ReportView from './pages/ReportView'
import SymphonyHub from './pages/SymphonyHub'
import Courses from './pages/Courses'
import CourseDetail from './pages/CourseDetail'
import CourseFeedback from './pages/CourseFeedback'
import Landing from './pages/Landing'
import ParaEmpresas from './pages/ParaEmpresas'
import ParaEmpresasDemo from './pages/ParaEmpresasDemo'
import NotFound from './pages/NotFound'
import { ProtectedRoute } from './components/ProtectedRoute'
import SupportChannelButton from './components/SupportChannelButton'
import { MaintenanceBanner } from './components/MaintenanceBanner'
import { TrophyNotification } from './components/trophy/TrophyNotification'
import { TrophyQueueProvider } from './hooks/use-trophy-queue'
import { Navigate } from 'react-router-dom'

const AuthRedirect = () => {
  const token = localStorage.getItem('token')
  return token ? <Navigate to="/home" replace /> : <Navigate to="/landing" replace />
}

const queryClient = new QueryClient()
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <TrophyQueueProvider>
              <SentryRoutes>
                <Route path="/" element={<AuthRedirect />} />
                <Route path="/home" element={<Index />} />
                <Route path="/cursos" element={<Courses />} />
                <Route path="/cursos/:slug" element={<CourseDetail />} />
                <Route path="/episode/:moduleId/:episodeId" element={<Episode />} />
                <Route path="/quiz/:moduleId/:episodeId" element={<Quiz />} />
                <Route path="/course-feedback/:courseId" element={<CourseFeedback />} />
                <Route path="/acesse-symphony" element={<AccessSymphony />} />
                <Route
                  path="/perfil"
                  element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  }
                />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/check-email" element={<CheckEmail />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/super-admin"
                  element={
                    <ProtectedRoute requireSuperAdmin={true}>
                      <SuperAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/super-admin/relatorios/:type"
                  element={
                    <ProtectedRoute requireSuperAdmin={true}>
                      <ReportView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hub"
                  element={
                    <ProtectedRoute>
                      <SymphonyHub />
                    </ProtectedRoute>
                  }
                />
                <Route path="/landing" element={<Landing />} />
                <Route path="/para-empresas" element={<ParaEmpresas />} />
                <Route path="/para-empresas/demo" element={<ParaEmpresasDemo />} />
                <Route path="*" element={<NotFound />} />
              </SentryRoutes>
              <SupportChannelButton />
              <TrophyNotification />
              <MaintenanceBanner />
            </TrophyQueueProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
)

export default App
